/**
 * Live catalog prices for unsent proposals.
 * Timber, post bases, decking, and hardware-list rows that match a catalog
 * product pick up the current materials-database price. Lines with no match
 * keep their stored cost. Empty catalogs are a no-op so a late subscribe
 * cannot wipe prices.
 */

import type {
  DeckingItem,
  HardwareListGroup,
  HardwareListItem,
  MaterialsNodeData,
  PostBasesItem,
  ProposalNodeSet,
  TimberListItem,
} from '../types';
import { resolveHardwareUnitPrice } from './hardwareCatalogDisplay';
import {
  type GlulamItem,
  type HardwareCategory,
  type HardwareItem,
  type TimberItem,
} from './materialsDatabaseService';
import { findProposalTimberPiecePrice } from './proposalTimberCatalog';
import { syncHardwareRollups } from './proposalHardwareCsvParser';
import {
  deckingItemTotal,
  resolveDeckingWidthInches,
} from './proposalStructureDecking';
import {
  getStructurePostBasesItems,
  withPostBasesRollups,
} from './proposalStructurePostBases';

const PRICE_EPS = 0.001;

function pricesDiffer(a: number, b: number): boolean {
  return Math.abs((Number(a) || 0) - (Number(b) || 0)) > PRICE_EPS;
}

function round2(n: number): number {
  return Math.round(n * 100) / 100;
}

function normalizeName(value: string | undefined | null): string {
  return (value || '').trim().toLowerCase().replace(/\s+/g, ' ');
}

export function findHardwareCatalogProduct(
  hardwareDb: HardwareItem[],
  category: HardwareCategory,
  productName?: string | null,
): HardwareItem | undefined {
  const want = normalizeName(productName);
  if (!want) return undefined;
  return hardwareDb.find(
    (item) => item.category === category && normalizeName(item.name) === want,
  );
}

function catalogUnitPrice(item: HardwareItem | undefined): number | null {
  if (!item) return null;
  const price = resolveHardwareUnitPrice(item);
  if (price == null || !Number.isFinite(price) || price <= 0) return null;
  return price;
}

function timberPiecePrice(
  timberDb: TimberItem[],
  glulamDb: GlulamItem[],
  item: Pick<TimberListItem, 'species' | 'grade' | 'dimensions' | 'moistureContent'>,
): number {
  return findProposalTimberPiecePrice(
    timberDb,
    glulamDb,
    item.species,
    item.grade,
    item.dimensions,
    item.moistureContent,
  );
}

function syncTimberList(
  list: TimberListItem[] | undefined,
  timberDb: TimberItem[],
  glulamDb: GlulamItem[],
): { list: TimberListItem[]; changed: boolean } {
  const source = list || [];
  if ((!timberDb.length && !glulamDb.length) || source.length === 0) return { list: source, changed: false };
  let changed = false;
  const next = source.map((item) => {
    const dbPrice = timberPiecePrice(timberDb, glulamDb, item);
    const current = Number(item.unitCost) || 0;
    if (dbPrice <= 0 || !pricesDiffer(dbPrice, current)) {
      return item;
    }
    changed = true;
    return { ...item, unitCost: dbPrice, total: round2(dbPrice * (item.quantity || 0)) };
  });
  return { list: next, changed };
}

function syncPostBases(
  materials: MaterialsNodeData,
  hardwareDb: HardwareItem[],
): { materials: MaterialsNodeData; changed: boolean } {
  if (!hardwareDb.length) return { materials, changed: false };
  const rows: PostBasesItem[] =
    Array.isArray(materials.postBasesItems) && materials.postBasesItems.length > 0
      ? materials.postBasesItems
      : getStructurePostBasesItems(materials);
  if (rows.length === 0) return { materials, changed: false };

  let changed = false;
  const nextRows = rows.map((row) => {
    const price = catalogUnitPrice(
      findHardwareCatalogProduct(hardwareDb, 'Post Bases', row.productName),
    );
    if (price == null || !pricesDiffer(price, row.unitCost)) return row;
    changed = true;
    return { ...row, unitCost: price, total: round2(price * (row.quantity || 0)) };
  });
  if (!changed) return { materials, changed: false };
  return { materials: withPostBasesRollups(materials, nextRows), changed: true };
}

function withDeckingRollups(
  materials: MaterialsNodeData,
  items: DeckingItem[],
): MaterialsNodeData {
  const priced = items.map((item) => {
    const widthInches = resolveDeckingWidthInches(item.productName, item.widthInches);
    const next = { ...item, widthInches };
    return { ...next, total: deckingItemTotal(next) };
  });
  const first = priced[0];
  return {
    ...materials,
    deckingItems: priced,
    deckingTotal: priced.reduce((sum, item) => sum + item.total, 0),
    deckingSqFt: priced.reduce((sum, item) => sum + (item.sqFt || 0), 0),
    deckingProductName: first?.productName,
    deckingWidthInches: first?.widthInches || 0,
    deckingPricePerLinFt: first?.pricePerLinFt || 0,
  };
}

function syncDecking(
  materials: MaterialsNodeData,
  hardwareDb: HardwareItem[],
): { materials: MaterialsNodeData; changed: boolean } {
  if (!hardwareDb.length) return { materials, changed: false };
  const rows: DeckingItem[] =
    Array.isArray(materials.deckingItems) && materials.deckingItems.length > 0
      ? materials.deckingItems
      : [];
  const source = rows.length > 0
    ? rows
    : materials.deckingProductName || (materials.deckingSqFt || 0) > 0
      ? [{
          id: 'legacy-decking',
          productName: materials.deckingProductName,
          widthInches: materials.deckingWidthInches || 0,
          sqFt: materials.deckingSqFt || 0,
          pricePerLinFt: materials.deckingPricePerLinFt || 0,
          total: materials.deckingTotal || 0,
        }]
      : [];
  if (source.length === 0) return { materials, changed: false };

  let changed = false;
  const nextRows = source.map((row) => {
    const product = findHardwareCatalogProduct(hardwareDb, 'Decking', row.productName);
    const price = catalogUnitPrice(product);
    if (price == null || !pricesDiffer(price, row.pricePerLinFt)) return row;
    changed = true;
    const widthInches = resolveDeckingWidthInches(
      row.productName,
      product?.widthInches ?? row.widthInches,
    );
    const next = { ...row, pricePerLinFt: price, widthInches };
    return { ...next, total: deckingItemTotal(next) };
  });
  if (!changed) return { materials, changed: false };
  return { materials: withDeckingRollups(materials, nextRows), changed: true };
}

function hardwareListCategory(group: HardwareListGroup): HardwareCategory | null {
  if (group === 'fasteners') return 'Fasteners';
  if (group === 'pegs') return 'Pegs';
  if (group === 'other') return 'Other';
  return null;
}

function findHardwareListCatalogMatch(
  hardwareDb: HardwareItem[],
  item: HardwareListItem,
): HardwareItem | undefined {
  const category = hardwareListCategory(item.group);
  if (category) {
    const byName = findHardwareCatalogProduct(hardwareDb, category, item.name);
    if (byName) return byName;
  }
  const sku = normalizeName(item.name);
  if (!sku) return undefined;
  return hardwareDb.find((row) => normalizeName(row.sku) === sku);
}

function syncHardwareList(
  list: HardwareListItem[] | undefined,
  hardwareDb: HardwareItem[],
): { list: HardwareListItem[] | undefined; changed: boolean } {
  if (!hardwareDb.length || !list?.length) return { list, changed: false };
  let changed = false;
  const next = list.map((item) => {
    const price = catalogUnitPrice(findHardwareListCatalogMatch(hardwareDb, item));
    if (price == null || !pricesDiffer(price, item.unitCost)) return item;
    changed = true;
    return { ...item, unitCost: price, total: round2(price * (item.quantity || 0)) };
  });
  return { list: next, changed };
}

/** Re-price one structure from the current timber + glulam + hardware catalogs. */
export function syncNodeSetCatalogPrices(
  nodeSet: ProposalNodeSet,
  timberDb: TimberItem[],
  hardwareDb: HardwareItem[],
  glulamDb: GlulamItem[] = [],
): ProposalNodeSet {
  let materials = nodeSet.materials;
  let changed = false;

  const defaultTimber = syncTimberList(materials.timberList, timberDb, glulamDb);
  if (defaultTimber.changed) {
    materials = {
      ...materials,
      timberList: defaultTimber.list,
      timberTotal: defaultTimber.list.reduce((sum, item) => sum + (item.total || 0), 0),
    };
    changed = true;
  }

  if (materials.alternateSpecies?.length && (timberDb.length || glulamDb.length)) {
    let altChanged = false;
    const alternateSpecies = materials.alternateSpecies.map((species) => {
      const synced = syncTimberList(species.timberList, timberDb, glulamDb);
      if (!synced.changed) return species;
      altChanged = true;
      return {
        ...species,
        timberList: synced.list,
        timberTotal: synced.list.reduce((sum, item) => sum + (item.total || 0), 0),
      };
    });
    if (altChanged) {
      materials = { ...materials, alternateSpecies };
      changed = true;
    }
  }

  const post = syncPostBases(materials, hardwareDb);
  materials = post.materials;
  changed = changed || post.changed;

  const decking = syncDecking(materials, hardwareDb);
  materials = decking.materials;
  changed = changed || decking.changed;

  const hardware = syncHardwareList(materials.hardwareList, hardwareDb);
  if (hardware.changed) {
    materials = { ...materials, hardwareList: hardware.list };
    changed = true;
  }

  if (!changed) return nodeSet;
  return { ...nodeSet, materials: syncHardwareRollups(materials) };
}

export function syncProposalNodeSetsCatalogPrices(
  nodeSets: ProposalNodeSet[],
  timberDb: TimberItem[],
  hardwareDb: HardwareItem[],
  glulamDb: GlulamItem[] = [],
): { nodeSets: ProposalNodeSet[]; changed: boolean } {
  if (!nodeSets.length || (!timberDb.length && !glulamDb.length && !hardwareDb.length)) {
    return { nodeSets, changed: false };
  }
  let changed = false;
  const next = nodeSets.map((ns) => {
    const synced = syncNodeSetCatalogPrices(ns, timberDb, hardwareDb, glulamDb);
    if (synced !== ns) changed = true;
    return synced;
  });
  return { nodeSets: next, changed };
}
